<template>
  <div
      class="vac-format-message-wrapper"
      :class="{ 'vac-text-ellipsis': singleLine }"
  >
    <a
        v-if="!deleted && linkPreview && linkPreview.url"
        class="vac-link-preview"
        :href="linkPreview.url"
        :target="linkOptions.target"
        :rel="linkOptions.rel"
    >
      <div class="vac-link-preview__thumb">
        <img
            v-if="linkPreview.favicon"
            class="vac-link-preview__thumb-img"
            :src="linkPreview.favicon"
            alt=""
            referrerpolicy="no-referrer"
        />
      </div>
      <div class="vac-link-preview__meta">
        <div class="vac-link-preview__title">{{ linkPreview.title }}</div>
        <div class="vac-link-preview__host">{{ linkPreview.host }}</div>
      </div>
    </a>

    <template v-for="(message, i) in parsedMessage" :key="i">
      <div
          v-if="message.markdown"
          class="markdown"
          @click="openTag"
          v-html="message.value"
      />
      <div
          v-else
          class="vac-format-container"
          :class="{ 'vac-text-ellipsis': singleLine }"
      >
        <component
            :is="message.url ? 'a' : 'span'"
            :class="{
						'vac-text-ellipsis': singleLine,
						'vac-text-tag': !singleLine && !reply && message.tag
					}"
            :href="message.href"
            :target="message.href ? linkOptions.target : null"
            :rel="message.href ? linkOptions.rel : null"
        >
          <template v-if="deleted">
            <slot
                :name="
								roomList
									? 'deleted-icon-room_' + roomId
									: 'deleted-icon_' + messageId
							"
            >
              <svg-icon
                  name="deleted"
                  class="vac-icon-deleted"
                  :class="{ 'vac-icon-deleted-room': roomList }"
              />
            </slot>
            {{ textMessages.MESSAGE_DELETED }}
          </template>
          <template v-else-if="message.url && message.image">
            <div class="vac-image-link-container">
              <div
                  class="vac-image-link"
                  :style="{
									'background-image': `url('${message.value}')`,
									height: message.height
								}"
              />
            </div>
            <div class="vac-image-link-message">
              {{ message.value }}
            </div>
          </template>
          <template v-else>
            {{ message.value }}
          </template>
        </component>
      </div>
    </template>
  </div>
</template>

<script>
import SvgIcon from '../SvgIcon/SvgIcon.vue'

import markdown from '@/modules/whatsapp/utils/markdown'
import {IMAGE_TYPES} from '@/modules/whatsapp/utils/constants'

export default {
  name: 'FormatMessage',
  components: {SvgIcon},

  props: {
    messageId: {type: String, default: ''},
    roomId: {type: String, default: ''},
    roomList: {type: Boolean, default: false},
    content: {type: [String, Number], required: true},
    deleted: {type: Boolean, default: false},
    users: {type: Array, default: () => []},
    linkify: {type: Boolean, default: true},
    singleLine: {type: Boolean, default: false},
    reply: {type: Boolean, default: false},
    textFormatting: {type: Object, required: true},
    textMessages: {
      type: Object, default: () => {
      }
    },
    linkOptions: {type: Object, required: true}
  },

  emits: ['open-user-tag'],

  computed: {
    linkPreview() {
      if (this.deleted) return null
      if (!this.linkify) return null
      if (this.singleLine) return null
      if (this.reply) return null

      const raw = String(this.content == null ? '' : this.content).trim()
      if (!raw) return null

      // Skip if it's just an image link (we already have image-link rendering)
      const imageLike = (u) => {
        const s = String(u || '').split('?')[0].split('#')[0].toLowerCase()
        return IMAGE_TYPES.some((t) => s.endsWith(`.${t}`))
      }

      const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/ig
      const candidates = raw.match(urlRegex) || []
      if (!candidates.length) return null

      const trimPunctuation = (u) => {
        let s = String(u || '').trim()
        if (!s) return ''
        while (/[\]\[\)\(\}\{<>.,!?;:'"，。！？；：”’》】）]+$/.test(s)) {
          s = s.replace(/[\]\[\)\(\}\{<>.,!?;:'"，。！？；：”’》】）]+$/, '')
        }
        return s
      }

      const normalize = (u) => {
        const s = trimPunctuation(u)
        if (!s) return ''
        if (/^www\./i.test(s)) return `https://${s}`
        return s
      }

      const pickFirstValid = () => {
        for (const c of candidates) {
          const u = normalize(c)
          if (!u) continue
          try {
            const urlObj = new URL(u)
            const ok = urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
            if (!ok) continue
            if (imageLike(urlObj.href)) continue
            return urlObj
          } catch (e) {
          }
        }
        return null
      }

      const urlObj = pickFirstValid()
      if (!urlObj) return null

      const host = String(urlObj.host || '').replace(/^www\./i, '')
      const title = host || urlObj.href
      const favicon = host
        ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=128`
        : ''

      return {
        url: urlObj.href,
        host,
        title,
        favicon
      }
    },
    parsedMessage() {
      if (this.deleted) {
        return [{value: this.textMessages.MESSAGE_DELETED}]
      }

      let options
      if (!this.textFormatting.disabled) {
        options = {
          textFormatting: {
            linkify: this.linkify,
            linkOptions: this.linkOptions,
            singleLine: this.singleLine,
            reply: this.reply,
            users: this.users,
            ...this.textFormatting
          }
        }
      } else {
        options = {}
      }

      const message = markdown(this.content, options)

      message.forEach(m => {
        m.markdown = this.checkType(m, 'markdown')
        m.tag = this.checkType(m, 'tag')
        m.image = this.checkImageType(m)
      })

      return message
    }
  },

  methods: {
    checkType(message, type) {
      return message.types && message.types.indexOf(type) !== -1
    },
    checkImageType(message) {
      let index = message.value.lastIndexOf('.')
      const slashIndex = message.value.lastIndexOf('/')
      if (slashIndex > index) index = -1

      const type = message.value.substring(index + 1, message.value.length)

      const isMedia =
          index > 0 && IMAGE_TYPES.some(t => type.toLowerCase().includes(t))

      if (isMedia) this.setImageSize(message)

      return isMedia
    },
    setImageSize(message) {
      const image = new Image()
      image.src = message.value

      image.addEventListener('load', onLoad)

      function onLoad(img) {
        const ratio = img.path[0].width / 150
        message.height = Math.round(img.path[0].height / ratio) + 'px'
        image.removeEventListener('load', onLoad)
      }
    },
    openTag(event) {
      const userId = event.target.getAttribute('data-user-id')
      if (!this.singleLine && userId) {
        const user = this.users.find(u => String(u._id) === userId)
        this.$emit('open-user-tag', user)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.vac-link-preview {
  display: flex;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  max-width: 420px;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.06);
  text-decoration: none;
  margin-bottom: 6px;
}

.vac-link-preview__thumb {
  width: 64px;
  height: 64px;
  flex: 0 0 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
}

.vac-link-preview__thumb-img {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
}

.vac-link-preview__meta {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10px 12px 10px 0;
}

.vac-link-preview__title {
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: #111;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.vac-link-preview__host {
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.55);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vac-link-preview:hover {
  filter: brightness(0.98);
}
</style>
